import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "@/lib/api";
import { BookOpen, Link2, Loader2, Layers } from "lucide-react";

export default function QuranConnections() {
  const [surahs, setSurahs] = useState([]);
  const [selectedSurah, setSelectedSurah] = useState(null);
  const [verses, setVerses] = useState([]);
  const [loadingSurahs, setLoadingSurahs] = useState(true);
  const [loadingVerses, setLoadingVerses] = useState(false);
  const [selectedVerseId, setSelectedVerseId] = useState(null);
  const [connections, setConnections] = useState(null);
  const [loadingConnections, setLoadingConnections] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingSurahs(true);
      try {
        const { data } = await api.get("/quran/surahs");
        setSurahs(data || []);
        if (data?.[0]) setSelectedSurah(data[0].number);
      } catch (e) {
        // ignore
      } finally {
        setLoadingSurahs(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSurah) return;
    (async () => {
      setLoadingVerses(true);
      setSelectedVerseId(null);
      setConnections(null);
      try {
        const { data } = await api.get(`/quran/surahs/${selectedSurah}/verses`);
        setVerses(data?.verses || []);
      } catch (e) {
        // ignore
      } finally {
        setLoadingVerses(false);
      }
    })();
  }, [selectedSurah]);

  const selectVerse = async (verse) => {
    setSelectedVerseId(verse.id);
    setLoadingConnections(true);
    try {
      const { data } = await api.get(`/quran/verses/${verse.id}/connections`);
      setConnections(data);
    } catch (e) {
      setConnections(null);
    } finally {
      setLoadingConnections(false);
    }
  };

  const activeSurah = surahs.find((s) => s.number === selectedSurah);

  return (
    <Layout title="Quran Connections" subtitle="quran-connections">
      <section className="mb-8">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-amber-700 mb-2">
          Module · 03
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
          Read verses. See the connections they share.
        </h2>
        <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed">
          Select a surah, tap any verse, and surface verses from other surahs
          that share the same theme.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Surah list */}
        <aside
          className="lg:col-span-3 rounded-md border border-slate-200 bg-white p-5 self-start"
          data-testid="quran-surah-list"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-700 mb-3">
            Surahs
          </div>
          {loadingSurahs ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <ul className="space-y-1">
              {surahs.map((s) => (
                <li key={s.number}>
                  <button
                    type="button"
                    onClick={() => setSelectedSurah(s.number)}
                    data-testid={`surah-item-${s.number}`}
                    className={`w-full text-left rounded-md px-3 py-2.5 transition-all duration-150 flex items-start gap-3 ${
                      selectedSurah === s.number
                        ? "bg-slate-900 text-white"
                        : "hover:bg-slate-100 text-slate-700"
                    }`}
                  >
                    <span
                      className={`font-mono text-[10px] mt-0.5 ${
                        selectedSurah === s.number ? "text-white/60" : "text-slate-400"
                      }`}
                    >
                      #{s.number}
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">
                        {s.name_english}
                      </div>
                      <div
                        className={`text-[11px] truncate ${
                          selectedSurah === s.number ? "text-white/60" : "text-slate-500"
                        }`}
                      >
                        {s.name_meaning} · {s.verse_count} verses
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Verses reader */}
        <section
          className="lg:col-span-5 rounded-md border border-slate-200 bg-white p-6"
          data-testid="quran-verses-panel"
        >
          {activeSurah && (
            <div className="mb-6 pb-5 border-b border-slate-200">
              <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                Surah #{activeSurah.number} · {activeSurah.revelation}
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <h3 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                  {activeSurah.name_english}
                </h3>
                <span
                  dir="rtl"
                  className="font-display text-2xl text-slate-700"
                >
                  {activeSurah.name_arabic}
                </span>
              </div>
              <div className="mt-1 text-sm text-slate-600">
                {activeSurah.name_meaning}
              </div>
            </div>
          )}

          {loadingVerses ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading verses…
            </div>
          ) : (
            <ol className="space-y-4">
              {verses.map((v) => (
                <li
                  key={v.id}
                  data-testid={`verse-item-${v.id}`}
                  className={`rounded-md border p-5 cursor-pointer transition-all duration-150 ${
                    selectedVerseId === v.id
                      ? "border-amber-400 bg-amber-50/60"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                  onClick={() => selectVerse(v)}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-amber-700">
                      Verse {v.verse_number}
                    </span>
                    <span className="font-mono text-[10px] text-slate-400">
                      #{selectedSurah}:{v.verse_number}
                    </span>
                  </div>
                  <div
                    dir="rtl"
                    className="font-display text-xl leading-relaxed text-slate-900"
                  >
                    {v.arabic}
                  </div>
                  <p className="mt-3 text-sm text-slate-600 italic leading-relaxed">
                    {v.translation}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Connections panel */}
        <aside
          className="lg:col-span-4 rounded-md border border-slate-200 bg-white p-5 self-start lg:sticky lg:top-24"
          data-testid="quran-connections-panel"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-700 mb-3 flex items-center gap-2">
            <Link2 className="h-3.5 w-3.5" /> Connections
          </div>

          {!selectedVerseId ? (
            <div className="rounded-md border border-dashed border-slate-300 p-6 text-center">
              <BookOpen className="h-5 w-5 text-slate-400 mx-auto mb-2" />
              <div className="text-sm text-slate-500">
                Tap any verse to see its thematic connections.
              </div>
            </div>
          ) : loadingConnections ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : connections ? (
            <div className="space-y-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                  Themes
                </div>
                <div className="flex flex-wrap gap-2">
                  {connections.themes?.length ? (
                    connections.themes.map((t) => (
                      <span
                        key={t.id}
                        data-testid={`theme-chip-${t.id}`}
                        className="font-mono text-[11px] uppercase tracking-wider px-2 py-1 rounded-sm border border-amber-200 bg-amber-50 text-amber-700"
                      >
                        {t.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-slate-500">No themes</span>
                  )}
                </div>
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                  <Layers className="h-3 w-3" /> Related verses
                </div>
                {connections.related_verses?.length ? (
                  <ul className="space-y-3">
                    {connections.related_verses.map((v) => (
                      <li
                        key={v.id}
                        data-testid={`related-verse-${v.id}`}
                        className="rounded-md border border-slate-200 bg-slate-50 p-3"
                      >
                        <div className="font-mono text-[10px] text-slate-500 mb-1">
                          Surah {v.surah_number} · v{v.verse_number}
                        </div>
                        <div
                          dir="rtl"
                          className="font-display text-base leading-relaxed text-slate-900"
                        >
                          {v.arabic}
                        </div>
                        <p className="mt-2 text-xs italic text-slate-600 leading-relaxed line-clamp-3">
                          {v.translation}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-slate-500">
                    No related verses found.
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </Layout>
  );
}
